import styled, { css } from 'styled-components';

interface StyledButtonProps {
  $variant?: 'primary' | 'secondary' | 'outline' | 'outline-dark';
  $size?: 'sm' | 'md' | 'lg';
}

const variants = {
  primary: css`
    background-color: ${({ theme }) => theme.colors.primaryDark};
    color: ${({ theme }) => theme.colors.white};
    border: 2px solid ${({ theme }) => theme.colors.primaryDark};
    box-shadow: 0 8px 14px rgba(107, 79, 62, 0.15);

    &:hover {
      background-color: ${({ theme }) => theme.colors.dark};
      border-color: ${({ theme }) => theme.colors.dark};
      transform: translateY(-2px);
      box-shadow: ${({ theme }) => theme.shadows.button};
    }
  `,
  secondary: css`
    background-color: ${({ theme }) => theme.colors.secondary};
    color: ${({ theme }) => theme.colors.primaryDark};
    border: 2px solid ${({ theme }) => theme.colors.secondary};

    &:hover {
      background-color: ${({ theme }) => theme.colors.tertiary};
      border-color: ${({ theme }) => theme.colors.tertiary};
      transform: translateY(-2px);
    }
  `,
  outline: css`
    background-color: transparent;
    color: ${({ theme }) => theme.colors.white};
    border: 2px solid rgba(255, 255, 255, 0.78);

    &:hover {
      background-color: rgba(255, 255, 255, 0.14);
      border-color: rgba(255, 255, 255, 0.95);
      transform: translateY(-2px);
    }
  `,
  'outline-dark': css`
    background-color: transparent;
    color: ${({ theme }) => theme.colors.primaryDark};
    border: 2px solid ${({ theme }) => theme.colors.primaryDark};

    &:hover {
      background-color: ${({ theme }) => theme.colors.primaryDark};
      color: ${({ theme }) => theme.colors.white};
      transform: translateY(-2px);
    }
  `,
};

const sizes = {
  sm: css`
    padding: 0.5rem 1.25rem;
    font-size: 0.85rem;
  `,
  md: css`
    padding: 0.75rem 2rem;
    font-size: 0.95rem;
  `,
  lg: css`
    padding: 0.9rem 2.5rem;
    font-size: 1rem;
  `,
};

export const StyledButton = styled.a<StyledButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-family: ${({ theme }) => theme.fonts.body};
  font-weight: 600;
  border-radius: 999px;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};
  text-decoration: none;
  letter-spacing: 0.02em;
  min-height: 48px;

  ${({ $variant = 'primary' }) => variants[$variant]}
  ${({ $size = 'md' }) => sizes[$size]}

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid
      ${({ theme, $variant }) => {
        if ($variant === 'secondary') return theme.colors.primaryDark;
        if ($variant === 'outline') return theme.colors.white;
        return theme.colors.white;
      }};
    outline-offset: 2px;
    box-shadow: 0 0 0 4px
      ${({ theme, $variant }) => {
        if ($variant === 'secondary')
          return 'rgba(107, 79, 62, 0.25)'; // primaryDark halo
        if ($variant === 'outline') return 'rgba(255, 255, 255, 0.25)';
        return 'rgba(255, 255, 255, 0.2)';
      }};
  }

  svg {
    font-size: 1.15em;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: 52px;
    padding-left: 1.2rem;
    padding-right: 1.2rem;
    border-radius: 16px;
    font-size: 0.98rem;
  }
`;

